/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthFlowsEnum, BeneficiaryViewType } from '@finxone-platform/form-action';
import {
  APP_ZONES,
  AccountContextOptions,
  AccountInfoType,
  AccountStatusType,
  AccountTransactionRequest,
  BeneficiaryRequirementsResponse,
  BulkPaymentResultList,
  CardInfoType,
  CurrencyExchangeTransactionType,
  DepositDetailsType,
  ExchangeCurrencyTransactionsResponse,
  PaginatedResponse,
  PaymentRailOptions,
  ScheduledPaymentResponseType,
  SecureCardTokenInfo,
  TermDepositAccountDetailsType,
  TransactionContextOptions,
  TransactionType,
  SpendingBudgetRequest,
  SpendingBudgetResponse,
  SpendingBudgetUpdateRequest,
  SpendingBudgetStatusResponse,
} from '@finxone-platform/shared/sys-config-types';
import {
  PaymentRequestListResponse,
  PaymentRequestPayload,
  PaymentRequestSummaryCounts,
} from '@finxone-platform/shared/utils';
import { Store } from '@ngxs/store';
import { Buffer } from 'buffer';
import { serviceUnavailable } from 'libs/shared/default-templates/src/lib/default-templates/cop-no-match-reasons';
import { BehaviorSubject, Observable, catchError, combineLatest, forkJoin, fromEvent, map } from 'rxjs';
import { SetCopResponseStateReason } from '../../actions/cop-response.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../../actions/progress-bar.action';
import { CardState } from '../../state/card.state';
import { PaymentRequestSummary } from '../../state/notifications.state';
import {
  UploadFileResponse,
  uploadFileRequest,
} from '../../utils/cta-button-actions/bulk-payments/bulk-payments.utils';
import { isAndroid, isIOS } from '../../utils/device-detection.utils';
import { buildQueryString } from '../../utils/query-params.utils';
import { setConfirmationToken } from '../../utils/utils';

import { formatNameAsUrl } from '../../utils/zone-url.utils';
import { ConfigService } from '../config-service/config-service.service';
import {
  AccountDebitCreditSummary,
  AccountDebitCreditUpdateSummary,
  AddAccountRequest,
  AddBeneficiaryRequest,
  BulkPaymentHistoryResponse,
  CheckPayeeResponse,
  DailyBalanceValue,
  GetBeneficiaryResponse,
  GetCardRequest,
  GetCardResponse,
  GetTotalWealthResponse,
  Payee,
  PaymentRequestorRequest,
  PaymentRequestorResponse,
  CancellablePaymentResponse,
  TransactionDetailResponse,
  TransactionInfoType,
  Transfer,
  TransferMoneyInternationalResponse,
  TransferMoneyRequest,
  TransferMoneyResponse,
  TransferReason,
  UpdateAccountResponse,
  UpdateBeneficiaryRequest,
  updateTransactionRequest,
} from './account.type';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { DataDefinition, DataDocument } from '@finxone-platform/shared/app-sdk';

export type AirwallexAuthorizeResponse = {
  authCode: string;
  codeVerifier: string;
  connectedAccountId: string;
  componentType?: 'kycRfi' | 'kyc';
  scopes?: string[];
  /** Environment the auth code was minted against; must be passed to the SDK verbatim. */
  env?: 'sandbox' | 'prod';
  clientId?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private baseUrl = '';
  accounts$: Observable<AccountInfoType[]>;

  private _current_transaction = new BehaviorSubject<any>('');
  readonly $current_transaction = this._current_transaction.asObservable();
  current_transaction: Transfer | undefined;
  public isCardOnXPaySubject = new BehaviorSubject<boolean | undefined>(false);
  public isCardOnXPay$ = this.isCardOnXPaySubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private router: Router,
    private store: Store,
  ) {
    this.configService.getApi('account_service').subscribe((response) => {
      this.baseUrl = response;
    });

    // This is used primarily for the text widgets so when we build the handlebar model the current card is on xPay is kept upto to date from wallet updates on the mobile device
    // ensures currentCardIsOnXPay is updated when is page is shown or if app triggers a wallet update
    setTimeout(() => {
      combineLatest([
        this.store.select(CardState.getCards),
        fromEvent(window, 'pageshow'),
        fromEvent(window, 'tokenizationResult'),
      ]).subscribe(([cards]) => {
        this.currentCardIsOnXPay(cards.currentCardDetail?.maskedPan ?? '****');
      });
    });
  }

  getWidgetStorage(widget: string, contract: string): Observable<DataDocument | undefined> {
    return this.http
      .get<DataDocument | null>(`${this.baseUrl}/widget-storage/${encodeURIComponent(widget)}`, {
        params: new HttpParams().set('contract', contract),
      })
      .pipe(map((document) => document ?? undefined));
  }

  setWidgetStorage(
    widget: string,
    contract: string,
    value: Record<string, unknown>,
    ifRevision?: number,
  ): Observable<DataDocument> {
    let params = new HttpParams().set('contract', contract);
    if (ifRevision !== undefined) params = params.set('ifRevision', String(ifRevision));
    return this.http.put<DataDocument>(
      `${this.baseUrl}/widget-storage/${encodeURIComponent(widget)}`,
      value,
      { params },
    );
  }

  removeWidgetStorage(widget: string, contract: string, ifRevision?: number): Observable<void> {
    let params = new HttpParams().set('contract', contract);
    if (ifRevision !== undefined) params = params.set('ifRevision', String(ifRevision));
    return this.http.delete<void>(`${this.baseUrl}/widget-storage/${encodeURIComponent(widget)}`, { params });
  }

  getWidgetStorageCatalog(widget: string): Observable<DataDefinition[]> {
    return this.http.get<DataDefinition[]>(
      `${this.baseUrl}/widget-storage/${encodeURIComponent(widget)}/catalog`,
    );
  }

  public currentCardIsOnXPay(currentCardMaskedPan: string) {
    if (!isAndroid() && !isIOS()) {
      this.isCardOnXPaySubject.next(undefined);
      return;
    }

    if (isAndroid()) {
      const flutterWebView = (window as any).flutter_inappwebview;
      if (flutterWebView) {
        flutterWebView
          .callHandler('getGooglePayTokenListHandler', [])
          .then((result: { tokenList: { fpanLastFour: string }[] }) => {
            const isOnXPay = result.tokenList?.some(
              (token) => token.fpanLastFour === currentCardMaskedPan.slice(-4),
            );
            this.isCardOnXPaySubject.next(isOnXPay);
          })
          .catch(() => {
            this.isCardOnXPaySubject.next(false);
          });
      }
      this.isCardOnXPaySubject.next(false);
    } else {
      // iPhone
      const flutterWebView = (window as any).flutter_inappwebview;
      if (flutterWebView) {
        flutterWebView
          .callHandler('getTokenList', [])
          .then((result: { tokenList: { primaryAccountSuffix: string }[] }) => {
            const isOnXPay = result.tokenList?.some(
              (token) => token.primaryAccountSuffix === currentCardMaskedPan.slice(-4),
            );
            this.isCardOnXPaySubject.next(isOnXPay);
          })
          .catch(() => {
            this.isCardOnXPaySubject.next(false);
          });
      }
      this.isCardOnXPaySubject.next(false);
    }
  }

  public getPaymentSchemes(
    countryCode?: string,
    currencyCode?: string,
  ): Observable<{ label: string; value: PaymentRailOptions }[]> {
    const params: Record<string, string | number> = {
      ...(countryCode && { countryCode }),
      ...(currencyCode && { currencyCode }),
    };

    return this.http
      .get<{ label: string; value: PaymentRailOptions }[]>(`${this.baseUrl}/beneficiaries/payment-schemes`, {
        params: params,
      })
      .pipe(
        catchError<any, Observable<boolean>>((_err) => {
          console.error(`Error getting payment schemes: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public getBeneficiaryRequirements(
    countryCode: string,
    currencyCode: string,
    scheme?: PaymentRailOptions,
  ): Observable<BeneficiaryRequirementsResponse> {
    const params: Record<string, string | number> = {
      ...(countryCode && { countryCode }),
      ...(currencyCode && { currencyCode }),
      ...(scheme && { scheme }),
    };
    return this.http
      .get<BeneficiaryRequirementsResponse>(
        `${this.baseUrl}/beneficiaries/beneficiary-bank-account-requirements`,
        {
          params: params,
        },
      )
      .pipe(
        catchError<any, Observable<boolean>>((_err) => {
          console.error(`Error getting beneficiary bank requirements: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getMonthlySummary(accountId: string): Observable<AccountDebitCreditSummary> {
    return this.http.get(`${this.baseUrl}/account/monthly-summary?accountId=${accountId}`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Error fetching monthy summary for accountId ${accountId}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getUpdatedTransactionSummary(
    payload: updateTransactionRequest,
  ): Observable<AccountDebitCreditUpdateSummary> {
    return this.http
      .post<TransferMoneyResponse>(`${this.baseUrl}/account/update-transactions-summary`, payload)
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`Error fetching monthy summary for accountId: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getAccountCard(accountId: string, page = 1, size = 20): Observable<PaginatedResponse<CardInfoType>> {
    return this.http
      .get<PaginatedResponse<CardInfoType>>(
        `${this.baseUrl}/cards?accountId=${accountId}&page=${page}&size=${size}&`,
      )
      .pipe(
        catchError<any, Observable<boolean>>((_err) => {
          console.error(`Error getting account cards for accountId ${accountId}: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getTransactionSummary(
    accountId: string,
    params: AccountTransactionRequest,
  ): Observable<
    PaginatedResponse<
      TransactionInfoType | ExchangeCurrencyTransactionsResponse | ScheduledPaymentResponseType
    >
  > {
    // Build the base URL and query parameters based on transaction type
    const { baseUrl, queryParams } = this.buildTransactionUrl(accountId, params);

    // Build the final URL with query string
    const url = `${baseUrl}${buildQueryString(queryParams)}`;

    return this.http
      .get<
        PaginatedResponse<
          TransactionInfoType | ExchangeCurrencyTransactionsResponse | ScheduledPaymentResponseType
        >
      >(url)
      .pipe(
        catchError<any, Observable<boolean>>((_err) => {
          console.error(
            `Error getting transaction summary for accountId ${accountId}: ${JSON.stringify(_err)}`,
          );
          throw _err;
        }),
      );
  }

  private buildTransactionUrl(accountId: string, params: AccountTransactionRequest) {
    const {
      page,
      size,
      dateFrom,
      dateTo,
      sort,
      status,
      type: transactionType,
      currency,
      searchValue,
      transactionContext,
    } = params;

    // `sort` is only ever set by callers that ask for it (buildQueryString drops
    // undefined), so requests that never carried it are byte-for-byte unchanged.
    const baseQueryParams = {
      page,
      size,
      from: dateFrom,
      to: dateTo,
      sort,
      currency,
    };

    if (params.transactionContext === TransactionContextOptions.SAVINGS_TRANSACTION) {
      return {
        baseUrl: `${this.baseUrl}/savings/${accountId}/transactions`,
        queryParams: baseQueryParams,
      };
    }

    // Scheduled payments is a unique context that doesn't fit into the regular transaction types, so we handle it separately here. It has its own endpoint.
    // The accountContext controls whether accountId is appended to the query params.
    if (transactionContext === TransactionContextOptions.SCHEDULED_PAYMENTS) {
      const includeAccountId =
        params.accountContext === AccountContextOptions.CURRENT_ACCOUNT ||
        params.accountContext === AccountContextOptions.SAVINGS_ACCOUNT;
      return {
        baseUrl: `${this.baseUrl}/payments/scheduled`,
        queryParams: includeAccountId ? { ...baseQueryParams, accountId } : baseQueryParams,
      };
    }

    const addStatusIfApplicable = (queryParams: any) => {
      if (status && status.length) return { ...queryParams, status };
      return queryParams;
    };
    // Handle regular transactions
    if (transactionType === TransactionType.ALL) {
      return {
        baseUrl: `${this.baseUrl}/account/${accountId}/transactions`,
        queryParams: baseQueryParams,
      };
    }
    if (transactionType === TransactionType.OUTGOING) {
      return {
        baseUrl: `${this.baseUrl}/account/${accountId}/outgoing-transactions`,
        queryParams: addStatusIfApplicable(baseQueryParams),
      };
    }
    // Handle currency exchange transactions
    if (this.isCurrencyExchangeTransaction(transactionType)) {
      const exchangeQueryParams = {
        page,
        size,
        from: dateFrom,
        to: dateTo,
        currency,
        query: searchValue,
      };
      if (transactionType === CurrencyExchangeTransactionType.CURRENCYALL) {
        return {
          baseUrl: `${this.baseUrl}/exchanges/${accountId}/transactions`,
          queryParams: exchangeQueryParams,
        };
      } else {
        const type =
          transactionType === CurrencyExchangeTransactionType.CURRENCYOUTGOING
            ? TransactionType.OUTGOING
            : TransactionType.INCOMING;
        return {
          baseUrl: `${this.baseUrl}/exchanges/${accountId}/transactions`,
          queryParams: {
            ...exchangeQueryParams,
            type,
          },
        };
      }
    }
    // Default to incoming transactions
    return {
      baseUrl: `${this.baseUrl}/account/${accountId}/incoming-transactions`,
      queryParams: addStatusIfApplicable(baseQueryParams),
    };
  }

  private isCurrencyExchangeTransaction(transactionType: any): boolean {
    return [
      CurrencyExchangeTransactionType.CURRENCYALL,
      CurrencyExchangeTransactionType.CURRENCYINCOMING,
      CurrencyExchangeTransactionType.CURRENCYOUTGOING,
    ].includes(transactionType);
  }

  checkPayee(checkPayeeRequest: Payee, beneficiaryFlowParam?: string): Observable<CheckPayeeResponse> {
    return this.http.post<Payee>(`${this.baseUrl}/beneficiaries/check-payee`, checkPayeeRequest).pipe(
      catchError<any, Observable<boolean>>((_err: HttpErrorResponse) => {
        if (_err.status === 503 && beneficiaryFlowParam) {
          this.store.dispatch(new SetCopResponseStateReason(serviceUnavailable));
          this.router.navigateByUrl(
            `/zones/${formatNameAsUrl(
              APP_ZONES.PAYMENT,
            )}/review-information-no-match-cop?flow=${beneficiaryFlowParam}`,
          );
        }
        console.error(`Error needs writing properly: ${JSON.stringify(_err.message)}`);
        throw _err;
      }),
    );
  }

  addBeneficiary(
    addBeneficiaryRequest: AddBeneficiaryRequest,
    accessToken?: string,
  ): Observable<GetBeneficiaryResponse> {
    let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.BENEFICIARY);
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http.post<GetBeneficiaryResponse>(`${this.baseUrl}/beneficiaries`, addBeneficiaryRequest, {
      headers,
    });
  }

  addCurrencyAccount(currency: { currency: string }): Observable<AccountInfoType> {
    return this.http.post<AccountInfoType>(`${this.baseUrl}/account`, currency);
  }

  removeBeneficiary(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/beneficiaries/${id}`);
  }

  updateBeneficiary(
    id: string,
    payload: UpdateBeneficiaryRequest,
    accessToken?: string,
  ): Observable<GetBeneficiaryResponse> {
    let confirmationToken = '';
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http.patch<GetBeneficiaryResponse>(`${this.baseUrl}/beneficiaries/${id}`, payload, {
      headers,
    });
  }

  getAccounts(params: AccountRepositorySearchParams): Observable<PaginatedResponse<AccountInfoType>> {
    return this.http
      .get<PaginatedResponse<AccountInfoType>>(`${this.baseUrl}/account/list`, {
        params: {
          ...params,
          status: params.status ?? [AccountStatusType.active],
          page: params.page ?? 1,
          size: params.size ?? 10,
        },
      })
      .pipe(
        map((v: PaginatedResponse<AccountInfoType>) => {
          return v;
        }),
        catchError<PaginatedResponse<AccountInfoType>, Observable<PaginatedResponse<AccountInfoType>>>(
          (_err, _caught) => {
            console.error(`Error getting accounts: ${JSON.stringify(_err)}`);
            throw _err;
          },
        ),
      );
  }

  public getDepositAccountInfo(type: DepositDetailsType[]): Observable<TermDepositAccountDetailsType> {
    return this.http
      .get<TermDepositAccountDetailsType>(`${this.baseUrl}/term-deposit/account-details`, {
        params: {
          type: type,
        },
      })
      .pipe(
        map((v: TermDepositAccountDetailsType) => {
          return v;
        }),
        catchError<TermDepositAccountDetailsType, Observable<TermDepositAccountDetailsType>>(
          (_err, _caught) => {
            console.error(`Error getting accounts: ${JSON.stringify(_err)}`);
            throw _err;
          },
        ),
      );
  }

  public getTotalWealth(baseCurrency: string): Observable<GetTotalWealthResponse> {
    return this.http.get<any>(this.baseUrl + `/wealth/total/${baseCurrency}`);
  }

  public deactivateLedgerAccount(accountId: string) {
    return this.http.delete(this.baseUrl + `/account/${accountId}`);
  }

  public updateBeneficiaryFavoriteStatus(beneficiaryId: string, isFavorite: boolean) {
    const url = `${this.baseUrl}/beneficiaries/favorite-beneficiary`;
    const payload = {
      beneficiaryId,
      action: isFavorite ? 'add' : 'remove',
    };

    return this.http.post<{ alertCodes: string[]; success: boolean }>(url, payload);
  }

  getBeneficiary(
    page = 1,
    size = 100,
    query?: string,
    currency?: string,
    beneficiaryViewType: BeneficiaryViewType = BeneficiaryViewType.ALL_LIST,
    accountId?: string,
  ): Observable<PaginatedResponse<GetBeneficiaryResponse>> {
    const isRecentlyPaid = beneficiaryViewType === BeneficiaryViewType.RECENTLY_PAID_LIST;
    const isFirstPage = page === 1;

    // determine endpoint + sort settings
    let endpoint = `${this.baseUrl}/beneficiaries`;
    const recentPaidEndPoint = `${this.baseUrl}/beneficiaries/paid-beneficiaries/${accountId}`;
    let sortKey: string | undefined = 'name';
    let sortOrder: string | undefined = 'ASC';

    if (beneficiaryViewType === BeneficiaryViewType.FAVORITE_LIST) {
      endpoint = `${this.baseUrl}/beneficiaries/favorite-beneficiaries`;
      sortOrder = 'DESC';
    } else if (isRecentlyPaid && isFirstPage) {
      endpoint = recentPaidEndPoint;
      sortKey = undefined;
      sortOrder = undefined;
    }
    //for new multicurrency account structure, if All or no currency is selected, dont send a currency through params and just return all beneficiaries.
    if (currency === 'All' || currency == '') {
      currency = undefined;
    }
    // build params dynamically
    const params: Record<string, string | number> = {
      page,
      size,
      ...(sortKey && { sortKey }),
      ...(sortOrder && { sortOrder }),
      ...(query && { query }),
      ...(currency && { currency }),
    };

    // special case: recently paid (merge 2 APIs)
    if (isRecentlyPaid && isFirstPage) {
      const api1$ = this.fetchBeneficiaryRecords(recentPaidEndPoint, params);
      const api2$ = this.fetchBeneficiaryRecords(`${this.baseUrl}/beneficiaries`, params);

      return forkJoin([api1$, api2$]).pipe(
        map(([res1, res2]) => {
          const limitedRes1 = res1.result.slice(0, 3); // only first 3 records

          // collect names from the first array
          const existingIds = new Set(limitedRes1.map((p) => p.id));

          // filter out duplicates from second array
          const filteredResult2 = res2.result.filter((p) => !existingIds.has(p.id));

          // merge while keeping pages intact
          const merged = [...limitedRes1, ...filteredResult2];

          return { ...res2, result: merged, totalItems: merged.length };
        }),
      );
    }

    // default: single API call
    return this.fetchBeneficiaryRecords(endpoint, params);
  }

  private fetchBeneficiaryRecords(endpoint: string, params: Record<string, string | number>) {
    return this.http.get<PaginatedResponse<GetBeneficiaryResponse>>(endpoint, { params });
  }

  /**
   * One-shot GET /beneficiaries with the native BeneficiarySearchParams-shaped
   * filters. Unlike getBeneficiary it applies no view-type routing, no sort
   * override and no recently-paid merge — it is the plain native listing, for
   * callers (the custom-widget `beneficiaries.list` operation) that thread the
   * params through verbatim. Omitted params are omitted from the wire, so the
   * backend's own defaults (page 1, size 20) apply.
   */
  listBeneficiaries(params: {
    page?: number;
    size?: number;
    name?: string;
    currency?: string;
    country?: string;
    accountNumber?: string;
  }): Observable<PaginatedResponse<GetBeneficiaryResponse>> {
    const queryParams: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') queryParams[key] = value;
    }
    return this.fetchBeneficiaryRecords(`${this.baseUrl}/beneficiaries`, queryParams);
  }

  getBeneficiaryDetail(bid: string): Observable<GetBeneficiaryResponse> {
    return this.http.get<GetBeneficiaryResponse>(`${this.baseUrl}/beneficiaries/${bid}`);
  }

  transferMoney(payload: TransferMoneyRequest): Observable<TransferMoneyResponse> {
    payload.amount = +payload.amount;
    return this.http.post<TransferMoneyResponse>(`${this.baseUrl}/payments`, payload).pipe(
      map((res: TransferMoneyResponse) => {
        return res;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Failed to process payment ${JSON.stringify(payload)}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  transferMoneyInternational(payload: TransferMoneyRequest): Observable<TransferMoneyInternationalResponse> {
    payload.amount = +payload.amount;
    return this.http.post<TransferMoneyInternationalResponse>(`${this.baseUrl}/payments`, payload).pipe(
      map((res: TransferMoneyInternationalResponse) => {
        return res;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Failed to process payment ${JSON.stringify(payload)}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  makePaymentRequest(payload: PaymentRequestorRequest): Observable<PaymentRequestorResponse> {
    payload.amount = +payload.amount;
    return this.http.post<PaymentRequestorResponse>(`${this.baseUrl}/payments/request`, payload).pipe(
      map((res: PaymentRequestorResponse) => {
        return res;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Failed to process payment ${JSON.stringify(payload)}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  checkWebhookNotification(): Observable<string> {
    return this.http.get<string>(this.baseUrl + '/account/notification-check').pipe(
      map((accounts: string) => {
        //console.log(accounts, 'accounts');
        return accounts;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<string>>((_err, _caught) => {
        console.error(`Error while checking webhook notification:`);
        throw _err;
      }),
    );
  }

  getChartData(parms: string): Observable<DailyBalanceValue[]> {
    return this.http.get<DailyBalanceValue[]>(this.baseUrl + '/account/daily-account-balance?' + parms);
  }

  setRoleInfo(token: string) {
    this.configService.setRoleInfo(token);
  }

  updateDeviceTokenOnOrgChanged(deviceToken: { tokens: string[] }): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/notifications/subscription-token`, deviceToken);
  }

  listProviderCapabilities(): Observable<{
    provider: string;
    capabilities: Array<{ name: string; description: string; version: number }>;
  }> {
    return this.http.get<{
      provider: string;
      capabilities: Array<{ name: string; description: string; version: number }>;
    }>(`${this.baseUrl}/account/provider-capabilities`);
  }

  invokeProviderCapability(
    capability: string,
    payload?: Record<string, unknown>,
  ): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/account/provider-capabilities/invoke`, { capability, payload });
  }

  /** Mints a PKCE auth code for an Airwallex embedded component (`kycRfi` by default, or `kyc`). */
  authorizeAirwallexRfi(body: {
    organisationId: string;
    userId: string;
    accountId?: string;
    connectedAccountId?: string;
    component?: 'kycRfi' | 'kyc';
  }): Observable<AirwallexAuthorizeResponse> {
    return this.http.post<AirwallexAuthorizeResponse>(
      `${this.baseUrl}/account/airwallex/rfi/authorize`,
      body,
    );
  }

  getAccountDataByUserId(id: string): Observable<AccountInfoType[]> {
    return this.http.get<AccountInfoType[]>(this.baseUrl + '/account/list?userId=' + id);
  }

  addAccount(addAccountRequest: AddAccountRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/account/${addAccountRequest.userId}`, addAccountRequest);
  }

  updateAccount(
    accountId: string,
    update: { name?: string; status?: AccountStatusType },
  ): Observable<UpdateAccountResponse> {
    return this.http.patch<UpdateAccountResponse>(`${this.baseUrl}/account/${accountId}`, update);
  }

  updateSavingAccount(accountId: string, update: { name?: string }): Observable<UpdateAccountResponse> {
    return this.http.patch<UpdateAccountResponse>(`${this.baseUrl}/savings/${accountId}`, update);
  }

  getCards(payload: GetCardRequest): Observable<GetCardResponse> {
    return this.http.get<GetCardResponse>(this.baseUrl + '/cards', {
      params: payload,
    });
  }

  getSecureToken(publicKey: string): Observable<SecureCardTokenInfo> {
    console.log(publicKey, 'publicKey');
    const payload = {
      accountId: 'A1225NNK',
      cardId: 'V120012MTR',
      publicKey: publicKey,
    };
    return this.http.post<SecureCardTokenInfo>(this.baseUrl + '/cards/secure-details', payload).pipe(
      map((res: SecureCardTokenInfo) => {
        console.log(res, 'Secure Res');
        return res;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Failed to process token ${JSON.stringify(payload)}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }
  async getRSAKeys(): Promise<{ publicKey: string; keyPair: CryptoKeyPair }> {
    this.store.dispatch(new AddProgressBarStack({ uniqueId: 'getRSAKeys' }));
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 4096,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt'],
    );
    const exported = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const exportedArray = new Uint8Array(exported);

    // Convert the array of numbers to a Base64-encoded string using Buffer
    const base64String = Buffer.from(exportedArray).toString('base64');
    this.store.dispatch(new RemoveProgressBarStack({ uniqueId: 'getRSAKeys' }));
    return { publicKey: base64String, keyPair: keyPair };
  }

  async decryptAccessToken(
    encryptedToken: string,
    encryptedSymmetricKey: string,
    encodedIv: string,
    privateKey: CryptoKey,
  ) {
    this.store.dispatch(new AddProgressBarStack({ uniqueId: 'decryptAccessToken' }));
    const decodedEncryptedSymmetricKey = this.base64ToUint8Array(encryptedSymmetricKey);
    const decryptedSymmetricKey = await crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      privateKey,
      decodedEncryptedSymmetricKey,
    );
    const aesKey = await crypto.subtle.importKey('raw', decryptedSymmetricKey, 'AES-GCM', true, [
      'encrypt',
      'decrypt',
    ]);
    const decodedIv = this.base64ToUint8Array(encodedIv);
    const decodedEncryptedToken = this.base64ToUint8Array(encryptedToken);
    const rawDecryptedToken = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: decodedIv,
      },
      aesKey,
      decodedEncryptedToken,
    );
    this.store.dispatch(new RemoveProgressBarStack({ uniqueId: 'decryptAccessToken' }));
    return new TextDecoder('utf-8').decode(new DataView(rawDecryptedToken));
  }

  base64ToUint8Array(base64: string) {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
  addCard(addCardRequest: GetCardRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/cards`, addCardRequest);
  }

  closeAccount(payload: GetCardRequest): Observable<GetCardResponse> {
    return this.http.get<GetCardResponse>(this.baseUrl + '/account/close', {
      params: payload,
    });
  }

  subscribeUser(): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + '/notifications/user');
  }
  getTransactionDetail(
    transactionId: string,
    transactionType: TransactionType,
  ): Observable<TransactionDetailResponse> {
    const url = `${this.baseUrl}/payments/${transactionType}/${transactionId}`;

    return this.http.get<TransactionDetailResponse>(url).pipe(
      catchError<any, Observable<boolean>>((_err) => {
        console.error(
          `Error getting with ${transactionType} transaction summary for transactionId ${transactionId}: ${JSON.stringify(
            _err,
          )}`,
        );
        throw _err;
      }),
    );
  }

  bankDocumentDownload(
    accountId: string,
    type: string,
    startDate: string,
    endDate: string,
    fileType = 'pdf',
    currency?: string,
  ): Observable<any> {
    const options = { responseType: 'blob' as 'json' };
    let baseUrl = `${this.baseUrl}/account/${accountId}/statements/${fileType}?templateKey=${type}&from=${startDate}&to=${endDate}`;
    if (currency) {
      baseUrl += `&currency=${currency}`;
    }
    return this.http.get<any>(baseUrl, options);
  }

  documentPaymentProofDownload(
    accountId: string,
    type: string,
    transactionId: string,
    transactionType: string,
    fileType = 'pdf',
  ): Observable<any> {
    const options = { responseType: 'blob' as 'json' };

    return this.http.get<any>(
      `${this.baseUrl}/account/${accountId}/transactions/${transactionId}/${fileType}?templateKey=${type}&transactionType=${transactionType}`,
      options,
    );
  }

  public getTransferReasons(
    accountId: string,
    country: string,
    category?: string,
  ): Observable<TransferReason[]> {
    let params = new HttpParams();
    if (category) {
      params = params.set('category', category);
    }
    return this.http.get<TransferReason[]>(
      `${this.baseUrl}/payments/${accountId}/transfer-reasons/${country}`,
      { params },
    );
  }

  bankDocumentDownloadUrl(
    accountId: string,
    type: string,
    startDate: string,
    endDate: string,
    fileType = 'pdf',
    currency?: string,
  ): string {
    let baseUrl = `${this.baseUrl}/account/${accountId}/statements/${fileType}?templateKey=${type}&from=${startDate}&to=${endDate}`;
    if (currency) {
      baseUrl += `&currency=${currency}`;
    }
    return baseUrl;
  }

  savingsBankDocumentDownload(
    accountId: string,
    type: string,
    startDate: string,
    endDate: string,
    fileType = 'pdf',
  ): Observable<Blob> {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(
      this.savingsBankDocumentDownloadUrl(accountId, type, startDate, endDate, fileType),
      options,
    );
  }

  savingsBankDocumentDownloadUrl(
    accountId: string,
    type: string,
    startDate: string,
    endDate: string,
    fileType = 'pdf',
  ): string {
    const baseUrl = `${this.baseUrl}/savings-account-statement/${accountId}/${fileType}?templateKey=${type}&from=${startDate}&to=${endDate}`;
    return baseUrl;
  }

  holdingStatementDownload(
    type: string,
    startDate: string,
    endDate: string,
    fileType = 'pdf',
  ): Observable<Blob> {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(
      `${this.baseUrl}/account/holding-statements/${fileType}?templateKey=${type}&from=${startDate}&to=${endDate}`,
      options,
    );
  }

  holdingStatementDownloadUrl(type: string, startDate: string, endDate: string, fileType = 'pdf'): string {
    return `${this.baseUrl}/account/holding-statements/${fileType}?templateKey=${type}&from=${startDate}&to=${endDate}`;
  }

  documentPaymentProofDownloadUrl(
    accountId: string,
    type: string,
    transactionId: string,
    transactionType: string,
    fileType = 'pdf',
  ): string {
    return `${this.baseUrl}/account/${accountId}/transactions/${transactionId}/${fileType}?templateKey=${type}&transactionType=${transactionType}`;
  }

  documentExchangeProofDownload(
    type: string,
    exchangeId: string,
    transactionType: string,
    fileType = 'pdf',
  ): Observable<any> {
    const options = { responseType: 'blob' as 'json' };

    return this.http.get<any>(
      `${this.baseUrl}/account/exchanges/${exchangeId}/${fileType}?templateKey=${type}&transactionType=${transactionType}`,
      options,
    );
  }
  documentExchangeProofDownloadUrl(
    type: string,
    exchangeId: string,
    transactionType: string,
    fileType = 'pdf',
  ): string {
    return `${this.baseUrl}/account/exchanges/${exchangeId}/${fileType}?templateKey=${type}&transactionType=${transactionType}`;
  }

  public keyProductInformationDownloadUrl(url: string): string {
    // Normalize slashes because inconsistent config values cause malformed URLs that break document downloads.
    const baseUrl = this.configService.config.api_endpoints?.['default']?.replace(/\/$/, '') || '';
    const path = url?.replace(/^\//, '') || '';
    return `${baseUrl}/${path}`;
  }

  updateBeneficiaryInfo(beneficiaryInfo: UpdateBeneficiaryRequest): Observable<GetBeneficiaryResponse> {
    return this.http.patch<GetBeneficiaryResponse>(
      `${this.baseUrl}/beneficiaries/${beneficiaryInfo.id}`,
      beneficiaryInfo,
    );
  }

  getPaymentRequestDetailsById(requestId: string): Observable<PaymentRequestSummary> {
    return this.http.get<PaymentRequestSummary>(`${this.baseUrl}/payments/request/${requestId}`);
  }
  proceedUploadFile(payload: uploadFileRequest): Observable<UploadFileResponse> {
    return this.http.post<UploadFileResponse>(`${this.baseUrl}/bulk-payments`, payload).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError<any, Observable<boolean>>((_err, _caught) => {
        console.error(`Error While Uploading CSV File: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getBulkPaymentHistory(
    page: number,
    size: number,
    searchValue: string,
  ): Observable<BulkPaymentHistoryResponse> {
    return this.http.get<BulkPaymentHistoryResponse>(
      `${this.baseUrl}/bulk-payments?page=${page}&size=${size}&query=${searchValue}`,
    );
  }
  getBulkPaymentById(
    page: number,
    size: number,
    masterId: string,
    searchValue: string,
  ): Observable<PaginatedResponse<BulkPaymentResultList>> {
    return this.http.get<PaginatedResponse<BulkPaymentResultList>>(
      `${this.baseUrl}/bulk-payments/${masterId}?page=${page}&size=${size}&metadataInclude=summary&query=${searchValue}`,
    );
  }
  addBulkPaymentBeneficiary(masterId: string, benShowInList: boolean): Observable<UploadFileResponse> {
    return this.http
      .post<UploadFileResponse>(`${this.baseUrl}/bulk-payments/${masterId}/add-beneficiary`, {
        showInBeneficiaryList: benShowInList,
      })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`Add beneficiary: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }
  deleteTransaction(masterId: string, id: string): Observable<UploadFileResponse> {
    return this.http
      .delete<UploadFileResponse>(`${this.baseUrl}/bulk-payments/${masterId}/transactions/${id}`, {})
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`deleteTransaction: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  bulkPaymentContinueAnywayAndWithSuggestion(
    masterId: string,
    transactionId: string,
    request: { suggestion: boolean; name?: string },
  ) {
    return this.http
      .patch(
        `${this.baseUrl}/bulk-payments/${masterId}/transactions/${transactionId}/check-suggestion`,
        request,
      )
      .pipe(
        catchError<any, Observable<boolean>>((_err, _) => {
          console.error(`bulkPaymentContinueAnywayAndWithSuggestion: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  makeBulkPayment(id: string, accessToken?: string): Observable<TransferMoneyResponse> {
    let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.BULK_PAYMENT);
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http
      .post<TransferMoneyResponse>(`${this.baseUrl}/bulk-payments/${id}/transfer-payments`, {}, { headers })
      .pipe(
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`Failed to process payment ${JSON.stringify(id)}: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  retryBulkPaymentTransfer(masterId: string, id: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/bulk-payments/${masterId}/transfer-payments/${id}/retry`, '');
  }

  cancelAllPayment(masterId: string): Observable<UploadFileResponse> {
    return this.http
      .post<UploadFileResponse>(`${this.baseUrl}/bulk-payments/${masterId}/transfer-payments/cancelled`, {})
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`All payment cancel: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public getPaymentRequest(
    page: number,
    size: number,
    status?: string,
    searchValue?: string,
  ): Observable<PaginatedResponse<PaymentRequestListResponse>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (status) {
      params = params.set('status', status);
    }
    if (searchValue) {
      params = params.set('query', searchValue);
    }
    return this.http.get<PaginatedResponse<PaymentRequestListResponse>>(`${this.baseUrl}/payments/requests`, {
      params,
    });
  }

  public generatePaymentRequest(payload: PaymentRequestPayload): Observable<PaymentRequestPayload> {
    let headers = {} as HttpHeaders;

    const isSmsAuthEnabled = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    ).smsAuthenticationEnabled;

    const { accessToken, ...otherRequest } = payload;
    if (isSmsAuthEnabled && payload?.approvedStatus !== 'rejected') {
      let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.PAYMENT_REQUEST);
      if (accessToken) {
        confirmationToken = accessToken;
      }
      headers = new HttpHeaders({
        confirmation: confirmationToken,
      });
    }
    return this.http
      .patch<PaymentRequestPayload>(`${this.baseUrl}/payments/requests`, otherRequest, { headers })
      .pipe(
        catchError((error) => {
          console.error('Error generating payment request', error);
          throw error;
        }),
      );
  }

  public getPaymentSummaryCounters(queryParams: string[]): Observable<PaymentRequestSummaryCounts> {
    let params = new HttpParams();
    queryParams.forEach((param) => {
      params = params.append(`include${param.charAt(0).toUpperCase() + param.slice(1)}`, 'true');
    });
    return this.http
      .get<PaymentRequestSummaryCounts>(`${this.baseUrl}/summary/counters`, {
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error getting payment summaries', error);
          throw error;
        }),
      );
  }

  public removeScheduledPayment(txnId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/payments/payment-reversal/${txnId}`, {});
  }

  public getCancellablePayments(
    pageNumber = 1,
    limit = 10,
  ): Observable<PaginatedResponse<CancellablePaymentResponse>> {
    return this.http.get<PaginatedResponse<CancellablePaymentResponse>>(
      `${this.baseUrl}/payments/open-for-reversal?page=${pageNumber}&limit=${limit}`,
    );
  }

  public upsertSpendingBudget(payload: SpendingBudgetRequest): Observable<SpendingBudgetResponse> {
    return this.http.post<SpendingBudgetResponse>(`${this.baseUrl}/spending-budget`, payload).pipe(
      catchError<any, Observable<SpendingBudgetResponse>>((_err) => {
        console.error(`Error upserting spending budget: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  public getSpendingBudget(accountId: string): Observable<SpendingBudgetResponse> {
    return this.http.get<SpendingBudgetResponse>(`${this.baseUrl}/spending-budget/${accountId}`).pipe(
      catchError<any, Observable<SpendingBudgetResponse>>((_err) => {
        console.error(`Error fetching spending budget for accountId ${accountId}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  public getSpendingBudgetStatus(accountId: string): Observable<SpendingBudgetStatusResponse> {
    return this.http
      .get<SpendingBudgetStatusResponse>(`${this.baseUrl}/spending-budget/${accountId}/status`)
      .pipe(
        catchError<any, Observable<SpendingBudgetStatusResponse>>((_err) => {
          console.error(
            `Error fetching spending budget status for accountId ${accountId}: ${JSON.stringify(_err)}`,
          );
          throw _err;
        }),
      );
  }

  public saveTransactionNote(payload: {
    accountId: string;
    transactionId: string;
    note: string;
  }): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/transaction-notes/`, payload);
  }
}

export class AccountRepositorySearchParams {
  public page?: number = 1;
  public size?: number = 10;
  public id?: string[];
  public name?: string;
  public currency?: string[];
  public country?: string[];
  public status?: AccountStatusType[] = [AccountStatusType.active];
}
